import {
  Ref,
  useCallback,
} from 'react';
import type {
  ReactElement,
  ReactNode,
} from 'react';

import {
  StyledCheckbox,
  StyledArea,
  StyledInput,
  StyledIcon,
  StyledContent,
  StyledTooltipCheckboxWrapper,
} from './style';
import type {
  OnCheckboxChange,
  CheckboxValue,
} from './types';

import { Hint } from '../Hint';
import type { HintProps } from '../Hint';

export type CheckboxProps = {
  /**
   * выбран ли данный чекбокс
   */
  checked: boolean;
  /**
   * name html-элемента input, а также аргумент колбэка onChange
   */
  name?: string;
  /**
   * value html-элемента input, а также аргумент колбэка onChange
   */
  value?: CheckboxValue;
  /**
   * выключен ли данный чекбокс
   */
  disabled?: boolean;
  /**
   * подсветка ошибки
   */
  hasError?: boolean;
  /**
   * подсветка предупреждения
   */
  hasWarning?: boolean;
  /**
   * находится ли данный чекбокс в неопределённом состоянии
   */
  indeterminate?: boolean;

  /**
   * текст/контент, всплывающий при наведении на чекбокс
   */
  helpText?: ReactNode;

  /**
   * Аргументы для тултипа
   */
  tooltipProps?: HintProps;

  /**
   * текст/контент, выводящийся рядом с чекбоксом
   */
  label?: ReactNode;

  /**
   * хэндлер изменения значения, вызывается с параметрами: (nextChecked, value, name)
   */
  onChange?: OnCheckboxChange;
};

interface BaseCheckboxProps extends Omit<CheckboxProps, 'helpText'> {
  innerRef?: Ref<HTMLLabelElement>;
}

function BaseCheckbox({
  value = '',
  checked,
  disabled = false,
  hasError = false,
  hasWarning = false,
  name = '',
  indeterminate = false,
  innerRef = undefined,

  label = undefined,

  onChange: onChangeProp = undefined,
  ...wrapperProps
}: BaseCheckboxProps): ReactElement {
  const onChange = useCallback(() => {
    if (onChangeProp) {
      onChangeProp(!checked, value, name);
    }
  }, [
    checked,
    value,
    name,
    onChangeProp,
  ]);

  const isCheckedStyle = checked && !indeterminate;

  return (
    <StyledCheckbox
      $isDisabled={disabled}
      $isChecked={isCheckedStyle}
      $isIndeterminate={indeterminate}
      $hasError={hasError}
      $hasWarning={hasWarning}
      {...wrapperProps}
      ref={innerRef}
    >
      <StyledInput
        type="checkbox"
        value={value}
        checked={checked}
        disabled={disabled}
        name={name}
        onChange={onChange}
      />

      <StyledArea
        $isDisabled={disabled}
        $isChecked={isCheckedStyle}
        $isIndeterminate={indeterminate}
        $hasError={hasError}
        $hasWarning={hasWarning}
      >
        {
          isCheckedStyle && (
            <StyledIcon
              width={12}
              height={10}
              viewBox="0 0 12 10"
            >
              <path
                d="M3.86663 7.59999L1.06663 4.79999L0.133301 5.73333L3.86663 9.46666L11.8666 1.46666L10.9333 0.533325L3.86663 7.59999Z"
              />
            </StyledIcon>
          )
        }

        {
          indeterminate && (
            <StyledIcon
              width={12}
              height={10}
              viewBox="0 0 448 512"
            >
              <path
                d="M416 208H32c-17.67 0-32 14.33-32 32v32c0 17.67 14.33 32 32 32h384c17.67 0 32-14.33 32-32v-32c0-17.67-14.33-32-32-32z"
              />
            </StyledIcon>
          )
        }
      </StyledArea>

      <StyledContent>
        {label}
      </StyledContent>
    </StyledCheckbox>
  );
}

export function Checkbox({ helpText = null, tooltipProps = { tooltip: helpText }, ...props }: CheckboxProps) {
  if (helpText) {
    return (
      <StyledTooltipCheckboxWrapper>
        <BaseCheckbox {...props} />

        <Hint {...tooltipProps} tooltip={helpText} />
      </StyledTooltipCheckboxWrapper>
    );
  }

  return <BaseCheckbox {...props} />;
}
